// ═══════════════════════════════════════════════════════════════
// REGLAS DE ASIGNACIÓN AUTOMÁTICA DE CASOS (B6.1)
// ═══════════════════════════════════════════════════════════════
//
// Al crear un caso, antes de persistirlo definitivamente, evaluamos
// reglas declarativas que pueden override `asignadoA` según:
//   - tipoCasoCodigo (ej: 'reclamo-ml')
//   - categoria
//   - prioridad (≥ alta, ≥ crítica, etc.)
//   - area
//   - rubroEmpresa (si está disponible en el contexto)
//
// Schema (colección top-level `reglasAsignacionCaso`):
//   {
//     codigo, nombre, descripcion,
//     condiciones: {
//       tipoCasoCodigo?: string | string[],
//       categoria?:      string | string[],
//       prioridadMin?:   'baja'|'normal'|'alta'|'critica',
//       area?:           string | string[],
//     },
//     accion: {
//       asignadoAUserId?: string,        // asignar a usuario específico
//       asignadoRol?:     string,        // ej: 'supervisor-postventa' → resolver a userId via lookup futuro
//     },
//     prioridad: number,                 // orden de evaluación, menor primero
//     activo: true,
//   }
//
// Si ninguna regla matchea, no se hace nada y el `asignadoA` queda
// como vino en `crearCaso(data)` (default = owner).
// ═══════════════════════════════════════════════════════════════

import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { forPlatformCreate, forUpdate } from './dbHelpers'

const COL_REGLAS = 'reglasAsignacionCaso'

const PRIORIDAD_ORDEN = { baja: 1, normal: 2, alta: 3, critica: 4 }

// ──────── ABM ────────

export async function listarReglasAsignacionCaso({ soloActivas = true } = {}) {
  const snap = await getDocs(collection(db, COL_REGLAS))
  let list = snap.docs.map(docConId)
  if (soloActivas) list = list.filter(r => r.activo !== false)
  list.sort((a, b) => (a.prioridad || 100) - (b.prioridad || 100))
  return list
}

export async function getReglaAsignacionCaso(reglaId) {
  const snap = await getDoc(doc(db, COL_REGLAS, reglaId))
  return snap.exists() ? docConId(snap) : null
}

export async function upsertReglaAsignacionCaso(reglaId, datos) {
  const data = {
    codigo: datos.codigo || null,
    nombre: datos.nombre || 'Regla',
    descripcion: datos.descripcion || '',
    condiciones: datos.condiciones || {},
    accion: datos.accion || {},
    prioridad: Number(datos.prioridad) || 100,
    activo: datos.activo !== false,
  }
  if (!reglaId) {
    const payload = forPlatformCreate(data)
    const ref = await addDoc(collection(db, COL_REGLAS), payload)
    return { id: ref.id, ...payload }
  }
  const payload = forUpdate(data)
  await updateDoc(doc(db, COL_REGLAS, reglaId), payload)
  return { id: reglaId, ...payload }
}

// ──────── Evaluación ────────

/**
 * Evalúa si una regla matchea un caso dado.
 */
function reglaMatchea(regla, casoCtx) {
  const c = regla.condiciones || {}

  if (c.tipoCasoCodigo) {
    const arr = Array.isArray(c.tipoCasoCodigo) ? c.tipoCasoCodigo : [c.tipoCasoCodigo]
    if (!arr.includes(casoCtx.tipoCasoCodigo)) return false
  }
  if (c.categoria) {
    const arr = Array.isArray(c.categoria) ? c.categoria : [c.categoria]
    if (!arr.includes(casoCtx.categoria)) return false
  }
  if (c.area) {
    const arr = Array.isArray(c.area) ? c.area : [c.area]
    if (!arr.includes(casoCtx.area)) return false
  }
  if (c.prioridadMin) {
    const min = PRIORIDAD_ORDEN[c.prioridadMin] || 0
    const actual = PRIORIDAD_ORDEN[casoCtx.prioridad] || 0
    if (actual < min) return false
  }
  return true
}

/**
 * Aplica reglas de asignación al contexto del caso. Retorna `null` si ninguna
 * matchea, o `{ asignadoAUserId, reglaAplicada }` si alguna matcheó.
 *
 * No tira; si falla la query log warn y retorna null (no rompe crearCaso).
 *
 * @param {Object} casoCtx — { tipoCasoCodigo, categoria, area, prioridad }
 * @returns {Promise<{asignadoAUserId, reglaAplicada} | null>}
 */
export async function aplicarReglasAsignacion(casoCtx) {
  try {
    const reglas = await listarReglasAsignacionCaso({ soloActivas: true })
    for (const regla of reglas) {
      if (reglaMatchea(regla, casoCtx)) {
        const uid = regla.accion?.asignadoAUserId
        if (uid) {
          return { asignadoAUserId: uid, reglaAplicada: { id: regla.id, codigo: regla.codigo, nombre: regla.nombre } }
        }
        // Si la regla tiene asignadoRol pero no resolverlo (mecanismo de lookup
        // futuro), por ahora la salteamos.
      }
    }
    return null
  } catch (e) {
    console.warn('[reglasCaso] aplicarReglasAsignacion falló:', e.message)
    return null
  }
}

// ──────── Window debug ────────
if (typeof window !== 'undefined') {
  window.__reglasCaso = {
    listarReglasAsignacionCaso, getReglaAsignacionCaso, upsertReglaAsignacionCaso,
    aplicarReglasAsignacion,
  }
}
